import React from 'react'
import FOOTER_COLUMNS, {
  ITEMS_GLOBAL_KEY,
  ITEMS_UNITED_STATES_KEY
} from './FOOTER_COLUMNS'
import FooterColumnTitle from './FooterColumnTitle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope } from '@fortawesome/free-solid-svg-icons'
import {
  faLinkedin,
  faYoutube,
  faTiktok,
  faInstagram,
  faFacebookF
} from '@fortawesome/free-brands-svg-icons'
import useStaticRandomId from '../../../hooks/useStaticRandomId'

import LogoRecoveryCom from '@/components/LogoRecoveryCom'
import pageIsForUnitedStates from '../../../domain/useCases/pageIsForUnitedStates'
import { isNonEmptyArray } from '../../../utils/checks'
import { getFooterSiteLabelURL } from '@/domain/footer/getFooterHomeCopy'
import { getTaxonomyPageDepartment } from '@/domain/department'

const UK_ADDRESS = 'Health Foundry, Canterbury House, 1 Royal Street, SE1 7LL'

const footerSocialLinks = [
  {
    href: '/contact/',
    icon: faEnvelope,
    label: 'link to Contact Us',
    title: 'Contact Us'
  },
  {
    href: 'https://www.linkedin.com/company/recoverydotcom',
    icon: faLinkedin,
    label: 'link to LinkedIn page',
    title: 'LinkedIn'
  },
  {
    href: 'https://www.youtube.com/@recoverydotcom',
    icon: faYoutube,
    label: 'link to YouTube page',
    title: 'YouTube'
  },
  {
    href: 'https://www.tiktok.com/@recoverydotcom',
    icon: faTiktok,
    label: 'link to TikTok account',
    title: 'TikTok'
  },
  {
    href: 'https://www.instagram.com/recoverydotcom/',
    icon: faInstagram,
    label: 'link to Instagram account',
    title: 'Instagram'
  },
  {
    href: 'https://facebook.com/recoverydotcom/',
    icon: faFacebookF,
    label: 'link to Facebook page',
    title: 'Facebook'
  }
]

const SocialMediaLink = ({ link }) => (
  <a
    className="mx-2 text-white first:ml-0"
    href={link.href}
    key={link.href}
    aria-label={link.label}
    title={link.title}
  >
    <FontAwesomeIcon
      className="size-[18px] text-lg"
      icon={link.icon}
      aria-label={link.label}
      title={link.title}
      titleId={useStaticRandomId()}
    />
  </a>
)

const SocialMediaLinks = ({ links }) => (
  <div className="social-media-links flex flex-wrap justify-start gap-3 md:flex-nowrap">
    {links.map((link) => (
      <SocialMediaLink
        key={link.href}
        link={link}
      />
    ))}
  </div>
)

const FooterLink = ({ href, text, target = '_self' }) => (
  <li className="mb-0.5">
    <a
      className="text-sm text-white no-underline md:hover:underline"
      target={target}
      href={href}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
    >
      {text}
    </a>
  </li>
)

const FooterColumn = ({ title, links, subHeading = null, index = null }) => (
  <div
    className="column flex min-w-[300px] flex-col md:min-w-[200px] md:max-w-[220px]"
    key={title.text}
  >
    <FooterColumnTitle {...title} />
    <ul className="mb-5 ml-0 mt-[5px] p-0">
      {links.map((link) => (
        <FooterLink
          key={`${link.text}-${link.href}`}
          href={link.href}
          text={link.text}
          target={link.target}
        />
      ))}
    </ul>

    {/* Render social links if this is the 5th column */}
    {index === 4 && (
      <>
        <FooterColumnTitle
          text="Connect with Us"
          href="/contact/"
        />
        <SocialMediaLinks links={footerSocialLinks} />
      </>
    )}

    {subHeading && (
      <>
        <FooterColumnTitle {...subHeading.title} />
        <ul className="mb-5 ml-0 mt-[5px] p-0">
          {subHeading.items &&
            subHeading.items.map((link) => {
              if (link.text === 'social-media-links') {
                return (
                  <div
                    key={link.text}
                    className="social-media-links flex flex-wrap justify-start gap-3 md:flex-nowrap"
                  >
                    <SocialMediaLinks links={footerSocialLinks} />
                  </div>
                )
              }

              return (
                <FooterLink
                  key={link.href}
                  href={link.href}
                  text={link.text}
                />
              )
            })}
        </ul>
      </>
    )}
  </div>
)

const CopyrightSection = ({
  getCurrentYear,
  footerSiteLabel,
  footerSiteURL
}) => (
  <div className="right mb-6.5 mt-0 block gap-2 text-center text-sm text-white md:mb-0 md:flex md:text-start">
    <span className="inherit">&copy; 2017-{getCurrentYear()} </span>
    <span className="home-link text-white underline ">
      <a
        className="text-white underline"
        href={footerSiteURL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={footerSiteLabel}
        title={footerSiteLabel}
      >
        {footerSiteLabel}
      </a>
    </span>
    <br />
    <a
      className="inherit"
      target="_blank"
      rel="noopener noreferrer"
      href="/careers/"
      aria-label='link to "Careers" page'
      title="Careers"
    >
      <span className="careers-link my-2 text-white underline sm:my-0">
        Careers
      </span>{' '}
    </a>
    <a
      className="inherit px-1 sm:px-1"
      target="_blank"
      rel="noopener noreferrer"
      href="/news/"
      aria-label='link to "News" page'
      title="News"
    >
      <span className="news-link my-2 text-white underline sm:my-0">News</span>{' '}
    </a>
    <a
      href="/privacy-policy/"
      className="inherit px-1 sm:px-1"
      aria-label='link to "Privacy Policy" page'
      title="Privacy Policy"
    >
      <span className="privacy-link my-2 text-white underline sm:my-0">
        Privacy Policy
      </span>{' '}
    </a>
    <a
      href="https://www.iubenda.com/privacy-policy/57057685/cookie-policy"
      className="inherit px-1 sm:px-1"
      aria-label='link to "Cookies" policy page'
      title="Cookies policy"
    >
      <span className="cookies-link my-2 text-white underline sm:my-0">
        Cookies
      </span>
    </a>
  </div>
)

const LogoSection = ({ isUnitedKingdomPage }) => (
  <div className="text-center md:mb-0 md:text-start">
    <div className="logoPolicy mb-2 flex flex-col items-center text-center md:mb-5">
      <div className="logo-container flex self-center md:block">
        <div className="flex h-6 min-w-[192px] items-center py-6 md:py-0">
          <LogoRecoveryCom className="text-primary-200" />
        </div>
      </div>
    </div>
    {isUnitedKingdomPage && (
      <p className="mb-2 text-[14px] text-white md:mb-5">{UK_ADDRESS}</p>
    )}
  </div>
)

export const Footer = ({
  isUnitedKingdomPage = false,
  className = '',
  term = {},
  location = {},
  departmentFooter = []
}) => {
  const getCurrentYear = () => {
    // Using `new Date().getFullYear()` will produce a hydration error once a
    // year. We're fine with this even if this is a performance gotcha – this is
    // better than triggering a rerender every single time the Footer mounts
    // just to set the date client-side.
    return new Date().getFullYear()
  }

  const pageIsInUnitedStates = pageIsForUnitedStates({ term, location })
  const itemListLocationKey = pageIsInUnitedStates
    ? ITEMS_UNITED_STATES_KEY
    : ITEMS_GLOBAL_KEY

  const department = getTaxonomyPageDepartment({ term })
  const { footerSiteLabel, footerSiteURL } = getFooterSiteLabelURL({
    department
  })

  const renderDepartmentFooter = () => {
    return departmentFooter.map((column, index) => (
      <FooterColumn
        key={column.title.text}
        title={column.title}
        links={column?.links || []}
        index={index}
      />
    ))
  }

  const renderDefaultFooterColumns = () => {
    return FOOTER_COLUMNS.map((column) => {
      const columnItems =
        column.items[itemListLocationKey] || column.items.global

      return (
        <FooterColumn
          key={column.title.text}
          title={column.title}
          links={columnItems}
          subHeading={column.subHeading}
        />
      )
    })
  }

  return (
    <div
      className={`footer relative mt-8 flex w-screen flex-col items-center overflow-hidden bg-primary-900 px-4 pb-9 pt-7 md:pb-10 md:pt-20 ${className}`}
    >
      <div className="footer_content mb-10 flex w-full max-w-screen-2xl flex-col items-center md:m-0">
        <div className="top lg:flex-no-wrap flex w-full max-w-screen-xl flex-row flex-wrap gap-x-10 px-0 py-5 md:justify-between md:px-8">
          {isNonEmptyArray(departmentFooter)
            ? renderDepartmentFooter()
            : renderDefaultFooterColumns()}
        </div>

        <div className="bottom flex w-full max-w-screen-xl flex-col flex-wrap justify-center border-t border-t-[#808080] py-5 text-center md:flex-row md:justify-between md:px-8 md:text-start">
          <LogoSection isUnitedKingdomPage={isUnitedKingdomPage} />
          <CopyrightSection
            getCurrentYear={getCurrentYear}
            footerSiteLabel={footerSiteLabel}
            footerSiteURL={footerSiteURL}
          />
        </div>
      </div>
    </div>
  )
}

export default Footer
